class CollectionFilter extends HTMLElement {
  constructor() {
    super();

    this.onApplyClick = this.onApplyClick.bind(this);
    this.handleRangeInput = this.handleRangeInput.bind(this);
  }

  get sectionId() {
    return this.getAttribute("data-section-id");
  }

  connectedCallback() {
    this.filterTabs = Array.from(this.querySelectorAll("accordion-toggle"));

    // inputs
    this.sortOptions = this.querySelectorAll("input[type='radio']");
    this.checkboxes = this.querySelectorAll("input[type='checkbox']");
    this.minRange = this.querySelector("input[data-min-value]");
    this.maxRange = this.querySelector("input[data-max-value]");

    // APPLY button
    this.applyBtn = this.querySelector("[data-collection-trigger]");

    // ✅ Do NOT auto-apply on change
    // (no listeners for checkbox/radio change)

    // keep min/max validation while typing
    if (this.minRange && this.maxRange) {
      this.minRange.addEventListener("input", this.handleRangeInput);
      this.maxRange.addEventListener("input", this.handleRangeInput);
    }

    // APPLY click
    if (this.applyBtn) {
      this.applyBtn.addEventListener("click", this.onApplyClick);
    }
  }

  disconnectedCallback() {
    if (this.applyBtn) {
      this.applyBtn.removeEventListener("click", this.onApplyClick);
    }
    if (this.minRange) {
      this.minRange.removeEventListener("input", this.handleRangeInput);
    }
    if (this.maxRange) {
      this.maxRange.removeEventListener("input", this.handleRangeInput);
    }
  }

  onApplyClick(e) {
    e.preventDefault();
    const url = this.buildUrlFromInputs();
    this.fetchAndRender(url);
  }

  buildUrlFromInputs() {
    const url = new URL(window.location.href);

    // reset pagination when applying new filters
    url.searchParams.delete("page");

    // SORT (Shopify expects "sort_by")
    const checkedSort = this.querySelector("input[type='radio']:checked");
    if (checkedSort) url.searchParams.set("sort_by", checkedSort.value);
    else url.searchParams.delete("sort_by");

    // Remove all existing filter.* params first to avoid stale filters
    // (Shopify filter params start with "filter.")
    [...url.searchParams.keys()].forEach((key) => {
      if (key.startsWith("filter.")) url.searchParams.delete(key);
    });

    // CHECKBOX FILTERS
    // IMPORTANT: checkbox name must be value.param_name and value must be value.value
    const checkedFilters = this.querySelectorAll(
      "input[type='checkbox']:checked",
    );
    checkedFilters.forEach((cb) => {
      if (!cb.name) return;
      url.searchParams.append(cb.name, cb.value);
    });

    // PRICE FILTER
    // Your inputs already have: data-param="{{ filter.min_value.param_name }}"
    if (this.minRange && this.maxRange) {
      const minParam = this.minRange.dataset.param;
      const maxParam = this.maxRange.dataset.param;

      // ensure no duplicates
      if (minParam) url.searchParams.delete(minParam);
      if (maxParam) url.searchParams.delete(maxParam);

      // only add if user has a value
      if (minParam && this.minRange.value !== "") {
        url.searchParams.set(minParam, this.minRange.value);
      }
      if (maxParam && this.maxRange.value !== "") {
        url.searchParams.set(maxParam, this.maxRange.value);
      }
    }

    return url;
  }

  fetchAndRender(url) {
    // Shopify section rendering
    url.searchParams.set("section_id", this.sectionId);

    fetch(url.toString())
      .then((res) => res.text())
      .then((html) => {
        const temp = document.createElement("div");
        temp.innerHTML = html;

        // keep open accordions
        const openFilters = this.filterTabs
          .map((tab, index) =>
            tab.classList.contains("accordion--open") ? index : -1,
          )
          .filter((index) => index !== -1);

        openFilters.forEach((index) => {
          const newTab = temp
            .querySelector(".collection-filters__content")
            ?.querySelectorAll("accordion-toggle")[index];

          if (newTab) newTab.classList.add("accordion--open");
        });

        // Replace collection HTML
        const newInner = temp.querySelector(".el-collection-inner");
        if (!newInner) return;

        document.querySelector(".el-collection-inner").innerHTML =
          newInner.innerHTML;

        // keep drawer open (your existing behavior)
        const drawer = document
          .querySelector(".el-collection-inner")
          .querySelector(".collection-filters");

        const overlay = document.querySelector(".collection-filter__overlay");

        if (drawer) {
          if (window.matchMedia("(max-width: 991px)").matches) {
            // ✅ mobile/tablet drawer: close after apply
            drawer.classList.remove("active");
            if (overlay) overlay.classList.remove("active");

            setFilterIcons(false);
          } else {
            // ✅ desktop side panel: keep open after apply
            drawer.classList.add("active");

            setFilterIcons(true);
          }
        }

        // Update browser URL (remove section_id)
        url.searchParams.delete("section_id");
        window.history.pushState({}, "", url.toString());

        // re-bind filter drawer triggers after DOM replace
        filterButtonTrigger();
      });
  }

  handleRangeInput(event) {
    const input = event.currentTarget;
    const value = parseInt(input.value, 10);

    if (Number.isNaN(value)) return;

    if (input.hasAttribute("data-min-value")) {
      const max = parseInt(this.maxRange.value, 10);
      if (!Number.isNaN(max) && value >= max) input.value = max - 1;
    } else {
      const min = parseInt(this.minRange.value, 10);
      if (!Number.isNaN(min) && value <= min) input.value = min + 1;
    }
  }
}

customElements.define("collection-filter", CollectionFilter);

class AccordionToggle extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = this.querySelector(".accordion__header");
    this.content = this.querySelector(".accordion__content");

    this.handleClick = this.handleClick.bind(this);

    this.header.addEventListener("click", this.handleClick);

    this.addMaxHeight();
  }

  disconnectedCallback() {
    this.header.removeEventListener("click", this.handleClick);
  }

  handleClick() {
    if (this.classList.contains("accordion--open")) {
      this.classList.remove("accordion--open");
    } else {
      this.classList.add("accordion--open");
    }
  }

  addMaxHeight() {
    this.style.setProperty(
      "--accordion-content-height",
      this.content.scrollHeight + "px",
    );
    console.log(this.content.scrollHeight);
  }
}

customElements.define("accordion-toggle", AccordionToggle);

// FILTER BUTTON
function filterButtonTrigger() {
  // prevent double-binding if called multiple times
  if (window.__filterTriggerBound) return;
  window.__filterTriggerBound = true;

  const activeSVG = `
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M15 5L5 15" stroke="#001334" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M5 5L15 15" stroke="#001334" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `;

  function getDrawer() {
    return document.querySelector("collection-filter.collection-filters");
  }

  function getOverlay() {
    return document.querySelector(".collection-filter__overlay");
  }

  function updateIcons(drawer) {
    const triggers = document.querySelectorAll(".filter__trigger");
    if (!triggers.length) return;

    const firstIcon = triggers[0].querySelector(".filter-trigger__icon");
    if (!firstIcon) return;

    // store default once (per page)
    if (!window.__filterDefaultSVG) {
      window.__filterDefaultSVG = firstIcon.innerHTML;
    }

    const isActive = drawer?.classList.contains("active");
    triggers.forEach((trigger) => {
      const icon = trigger.querySelector(".filter-trigger__icon");
      if (!icon) return;
      icon.innerHTML = isActive ? activeSVG : window.__filterDefaultSVG;
    });
  }

  // Click on trigger (works even after re-render)
  document.addEventListener("click", (e) => {
    const trigger = e.target.closest(".filter__trigger");
    if (!trigger) return;

    const drawer = getDrawer();
    const overlay = getOverlay();
    if (!drawer || !overlay) return;

    drawer.classList.toggle("active");
    overlay.classList.toggle("active");
    updateIcons(drawer);
  });

  // Click on overlay to close
  document.addEventListener("click", (e) => {
    const overlayClicked = e.target.closest(".collection-filter__overlay");
    if (!overlayClicked) return;

    const drawer = getDrawer();
    const overlay = getOverlay();
    if (!drawer || !overlay) return;

    drawer.classList.remove("active");
    overlay.classList.remove("active");
    updateIcons(drawer);
  });

  // run once to sync icons with initial state
  updateIcons(getDrawer());
}

function setFilterIcons(isActive) {
  const triggers = document.querySelectorAll(".filter__trigger");
  if (!triggers.length) return;

  // get default from the current DOM the first time
  const firstIcon = triggers[0].querySelector(".filter-trigger__icon");
  if (!firstIcon) return;

  if (!window.__filterDefaultSVG) {
    window.__filterDefaultSVG = firstIcon.innerHTML;
  }

  const activeSVG = `
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M15 5L5 15" stroke="#001334" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M5 5L15 15" stroke="#001334" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `;

  triggers.forEach((trigger) => {
    const icon = trigger.querySelector(".filter-trigger__icon");
    if (!icon) return;
    icon.innerHTML = isActive ? activeSVG : window.__filterDefaultSVG;
  });
}

filterButtonTrigger();
